import { db } from '../config/firebase';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { User } from './firebaseAuth';
import { EmailNotificationService } from './emailNotificationService';
import { UserDocumentService } from './userDocumentService';
import { UserDocument } from '../types/userDocument';

export class AdminService {
  // Get all pending users for approval
  static async getPendingUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      // Get all users and filter in memory to avoid index requirements
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, uid: doc.id } as User;
      });
      
      // Filter and sort in memory
      const pendingUsers = users.filter(user => user.status === 'pending');
      return pendingUsers.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        return 0;
      });
    } catch (error) {
      console.error('Error getting pending users:', error);
      throw new Error('Failed to fetch pending users');
    }
  }

  // Get all approved users
  static async getApprovedUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      // Get all users and filter in memory to avoid index requirements
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, uid: doc.id } as User;
      });
      
      // Filter and sort in memory
      const approvedUsers = users.filter(user => user.status === 'approved');
      return approvedUsers.sort((a, b) => {
        if (a.approvedAt && b.approvedAt) {
          return new Date(b.approvedAt).getTime() - new Date(a.approvedAt).getTime();
        }
        return 0;
      });
    } catch (error) {
      console.error('Error getting approved users:', error);
      throw new Error('Failed to fetch approved users');
    }
  }

  // Get all rejected users
  static async getRejectedUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      // Get all users and filter in memory to avoid index requirements
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, uid: doc.id } as User;
      });
      
      // Filter and sort in memory
      const rejectedUsers = users.filter(user => user.status === 'rejected');
      return rejectedUsers.sort((a, b) => {
        if (a.approvedAt && b.approvedAt) {
          return new Date(b.approvedAt).getTime() - new Date(a.approvedAt).getTime();
        }
        return 0;
      });
    } catch (error) {
      console.error('Error getting rejected users:', error);
      throw new Error('Failed to fetch rejected users');
    }
  }

  // Get all suspended users
  static async getSuspendedUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      // Get all users and filter in memory to avoid index requirements
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, uid: doc.id } as User;
      });
      
      // Filter and sort in memory
      const suspendedUsers = users.filter(user => user.status === 'suspended');
      return suspendedUsers.sort((a, b) => {
        if (a.approvedAt && b.approvedAt) {
          return new Date(b.approvedAt).getTime() - new Date(a.approvedAt).getTime();
        }
        return 0;
      });
    } catch (error) {
      console.error('Error getting suspended users:', error);
      throw new Error('Failed to fetch suspended users');
    }
  }

  // Get all users (for admin overview)
  static async getAllUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      // Get all users without query to avoid index requirements
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, uid: doc.id } as User;
      });
      
      // Sort in memory
      return users.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        return 0;
      });
    } catch (error) {
      console.error('Error getting all users:', error);
      throw new Error('Failed to fetch users');
    }
  }

  // Get users with their account data (demo and real accounts)
  static async getUsersWithAccountData(): Promise<(User & { userDocument?: UserDocument | null })[]> {
    try {
      const users = await this.getAllUsers();
      const usersWithAccountData = await Promise.all(
        users.map(async (user) => {
          try {
            const userDocument = await UserDocumentService.getUserDocument(user.uid);
            return { ...user, userDocument };
          } catch (error) {
            console.warn(`Failed to fetch user document for ${user.uid}:`, error);
            return { ...user, userDocument: null };
          }
        })
      );
      
      return usersWithAccountData;
    } catch (error) {
      console.error('Error getting users with account data:', error);
      throw new Error('Failed to fetch users with account data');
    }
  }

  // Approve a user
  static async approveUser(userId: string, adminUid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'approved',
        approvedBy: adminUid,
        approvedAt: new Date()
      });

      // Send approval email
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.email && userData.displayName) {
            await EmailNotificationService.sendApprovalEmail(userData.email, userData.displayName);
          }
        }
      } catch (emailError) {
        console.warn('Failed to send approval email:', emailError);
      }
    } catch (error) {
      console.error('Error approving user:', error);
      throw new Error('Failed to approve user');
    }
  }

  // Reject a user
  static async rejectUser(userId: string, adminUid: string, reason: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'rejected',
        approvedBy: adminUid,
        approvedAt: new Date(),
        rejectionReason: reason
      });

      // Send rejection email
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.email && userData.displayName) {
            await EmailNotificationService.sendRejectionEmail(userData.email, userData.displayName, reason);
          }
        }
      } catch (emailError) {
        console.warn('Failed to send rejection email:', emailError);
      }
    } catch (error) {
      console.error('Error rejecting user:', error);
      throw new Error('Failed to reject user');
      }
  }

  // Suspend a user
  static async suspendUser(userId: string, adminUid: string, reason: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'suspended',
        approvedBy: adminUid,
        approvedAt: new Date(),
        rejectionReason: reason
      });

      // Send suspension email
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.email && userData.displayName) {
            await EmailNotificationService.sendRejectionEmail(userData.email, userData.displayName, reason);
          }
        }
      } catch (emailError) {
        console.warn('Failed to send suspension email:', emailError);
      }
    } catch (error) {
      console.error('Error suspending user:', error);
      throw new Error('Failed to suspend user');
    }
  }

  // Reapprove a rejected user
  static async reapproveUser(userId: string, adminUid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'approved',
        approvedBy: adminUid,
        approvedAt: new Date(),
        rejectionReason: null // Clear rejection reason
      });

      // Send reapproval email
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.email && userData.displayName) {
            await EmailNotificationService.sendApprovalEmail(userData.email, userData.displayName);
          }
        }
      } catch (emailError) {
        console.warn('Failed to send reapproval email:', emailError);
      }
    } catch (error) {
      console.error('Error reapproving user:', error);
      throw new Error('Failed to reapprove user');
    }
  }

  // Unsuspend a suspended user
  static async unsuspendUser(userId: string, adminUid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'approved',
        approvedBy: adminUid,
        approvedAt: new Date(),
        rejectionReason: null // Clear suspension reason
      });

      // Send unsuspension email
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.email && userData.displayName) {
            await EmailNotificationService.sendApprovalEmail(userData.email, userData.displayName);
          }
        }
      } catch (emailError) {
        console.warn('Failed to send unsuspension email:', emailError);
      }
    } catch (error) {
      console.error('Error unsuspending user:', error);
      throw new Error('Failed to unsuspend user');
    }
  }

  // Change user role (with role hierarchy restrictions)
  static async changeUserRole(userId: string, newRole: User['role'], adminUid: string, currentUserRole: User['role']): Promise<void> {
    try {
      // Role hierarchy restrictions
      if (currentUserRole === 'admin' && newRole === 'owner') {
        throw new Error('Admins cannot assign owner role. Only owners can create other owners.');
      }
      
      if (currentUserRole === 'trader' || currentUserRole === 'user') {
        throw new Error('You do not have permission to change user roles.');
      }
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        approvedBy: adminUid,
        approvedAt: new Date()
      });
    } catch (error) {
      console.error('Error changing user role:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to change user role';
      throw new Error(errorMessage);
    }
  }

  // Get user statistics
  static async getUserStats(): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    suspended: number;
  }> {
    try {
      const users = await this.getAllUsers();
      return {
        total: users.length,
        pending: users.filter(u => u.status === 'pending').length,
        approved: users.filter(u => u.status === 'approved').length,
        rejected: users.filter(u => u.status === 'rejected').length,
        suspended: users.filter(u => u.status === 'suspended').length
      };
    } catch (error) {
      console.error('Error getting user stats:', error);
      throw new Error('Failed to fetch user statistics');
    }
  }

  // Send KYC reminder emails to users who haven't completed KYC
  static async sendKYCReminders(): Promise<{ success: number; failed: number }> {
    try {
      const users = await this.getAllUsers();
      const usersNeedingReminders = users.filter(user => 
        user.status === 'pending' && !user.kycDocuments
      );

      let successCount = 0;
      let failedCount = 0;

      for (const user of usersNeedingReminders) {
        try {
          if (user.email && user.displayName) {
            await EmailNotificationService.sendKYCReminderEmail(user.email, user.displayName);
            successCount++;
          }
        } catch (error) {
          console.error(`Failed to send reminder to ${user.email}:`, error);
          failedCount++;
        }
      }

      return { success: successCount, failed: failedCount };
    } catch (error) {
      console.error('Error sending KYC reminders:', error);
      throw new Error('Failed to send KYC reminders');
    }
  }

  // Send KYC failed emails to users whose KYC was rejected
  static async sendKYCFailedEmails(): Promise<{ success: number; failed: number }> {
    try {
      const users = await this.getAllUsers();
      const usersWithFailedKYC = users.filter(user => 
        user.status === 'rejected' && user.rejectionReason
      );

      let successCount = 0;
      let failedCount = 0;

      for (const user of usersWithFailedKYC) {
        try {
          if (user.email && user.displayName && user.rejectionReason) {
            await EmailNotificationService.sendKYCFailedEmail(user.email, user.displayName, user.rejectionReason);
            successCount++;
          }
        } catch (error) {
          console.error(`Failed to send KYC failed email to ${user.email}:`, error);
          failedCount++;
        }
      }

      return { success: successCount, failed: failedCount };
    } catch (error) {
      console.error('Error sending KYC failed emails:', error);
      throw new Error('Failed to send KYC failed emails');
    }
  }
}
